import {
  FormSubmit,
  useDialogContext,
  useFormContext,
  type FormInputProps,
} from "@ariakit/react";
import { LetterheadSubmitError, Success, useForm } from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { useState } from "react";
import { database } from "../../database.ts";
import { type Gang } from "../../engine/Gang.ts";
import { GangMode } from "../../engine/GangMode.ts";
import { FormDialog, FormDialogActions } from "../FormDialog/FormDialog.tsx";
import { useHandleSuccess } from "../FormDialog/useHandleSuccess.ts";
import { FormDialogSubmitButton } from "../forms/DialogSubmitButton.tsx";
import { FormTextField } from "../forms/FormTextField.tsx";
import { useGangContext } from "../GangPage/useGang.ts";
import { GenerateNameHint } from "../GitFormDialog/GenerateNameHint.tsx";
import { useLatestRuleset } from "../rulesets.tsx";
import { arrowSubmit, gangFormDialog } from "./style.css.ts";

// Ariakit currently doesn't directly export StringLike, so we have to
// get it indirectly from another type.
type StringLike = FormInputProps["name"];

type GangModeType = SpaceGits.GangModeData["type"];

type GangFormValues = {
  name: string;
  modeType: GangModeType;
};

function SubmitArrowButton(
  props: {
    label: string;
    description: string;
    value: GangModeType;
    name: StringLike;
  } & RecipeVariants<typeof arrowSubmit.container>,
) {
  const { label, description, value, name, theme } = props;
  const form = useFormContext();

  return (
    <FormSubmit
      className={arrowSubmit.container({ theme })}
      onClick={() => void form?.setValue(name, value)}
    >
      <div className={arrowSubmit.label}>{label}</div>
      <div className={arrowSubmit.description}>{description}</div>
    </FormSubmit>
  );
}

export function GangFormDialog(props: {
  gang?: Gang;
  heading: string;
  overline?: string;
  submit: string;
  onSubmit: (
    data: Pick<SpaceGits.GangData, "name" | "mode">,
  ) => Promise<Success<string>>;
  onSuccess: (value: string) => { message?: string; navigateTo?: string };
}) {
  const {
    gang,
    heading,
    overline,
    submit,
    onSubmit: handleSubmit,
    onSuccess,
  } = props;
  const latestRuleset = useLatestRuleset();
  const dialog = useDialogContext();
  const handleSuccess = useHandleSuccess(dialog);
  const { gangNameGenerator } = latestRuleset;
  const [defaultGangName] = useState(gangNameGenerator.generate());

  const defaultValues: GangFormValues = {
    name: gang?.data.name ?? "",
    modeType: gang?.data.mode?.type ?? "A_LA_CARTE",
  };

  const { form, submitName } = useForm({
    defaultValues: defaultValues,
    async onSubmit({ values }) {
      const shouldGenerateName = !gang && !values.name;

      return handleSubmit({
        name: shouldGenerateName ? defaultGangName : values.name,

        // Once a gang exists, their mode should not be changed by this dialog
        mode: gang?.mode.data ?? GangMode.initData(values.modeType),
      });
    },

    async onSuccess(gangId) {
      await handleSuccess(onSuccess(gangId));
    },
  });

  return (
    <FormDialog heading={heading} overline={overline} form={form}>
      <FormTextField
        name={form.names.name}
        label="Name"
        autoFocusOnWide
        placeholder={`E.g. ${defaultGangName}`}
        hint={
          <GenerateNameHint
            generator={gangNameGenerator}
            onGenerate={(name) => void form.setValue(form.names.name, name)}
          />
        }
      />

      {!gang ? (
        <div style={{ marginBlock: "auto" }}>
          <LetterheadSubmitError name={submitName} />

          <div className={gangFormDialog.buildTypeInfo}>
            Pick a build mode...
          </div>

          <div className={gangFormDialog.arrows}>
            <SubmitArrowButton
              label="A La Carte"
              description="Standard list building, you know the drill..."
              value="A_LA_CARTE"
              name={form.names.modeType}
              theme="moss"
            />
            <SubmitArrowButton
              label="Chef's Recommendation"
              description="Pick you gits, get random loadout. Controlled chaos."
              value="CHEFS_REC"
              name={form.names.modeType}
              theme="lime"
            />
            <SubmitArrowButton
              label="Chaos Mode"
              description="Fully random. Great for a fun hobby challenge!"
              value="CHAOS_MODE"
              name={form.names.modeType}
              theme="red"
            />
          </div>
        </div>
      ) : (
        <FormDialogActions>
          <LetterheadSubmitError name={submitName} />

          <FormDialogSubmitButton>{submit}</FormDialogSubmitButton>
        </FormDialogActions>
      )}
    </FormDialog>
  );
}

export function CreateGangDialog() {
  const latestRuleset = useLatestRuleset();

  return (
    <GangFormDialog
      overline="City of Gnork"
      heading="Gang Registration Form"
      submit="Create"
      onSubmit={(data) => {
        return database.gangs.createOne({
          ...data,
          rulesetVersion: latestRuleset.version,
        });
      }}
      onSuccess={(gangId) => {
        return { navigateTo: `~/gangs/g/${gangId}` };
      }}
    />
  );
}

export function EditGangDialog() {
  const { gang } = useGangContext();
  return (
    <GangFormDialog
      heading="Edit gang"
      submit="Save"
      gang={gang}
      onSubmit={(data) => database.gangs.patchOne(gang, data)}
      onSuccess={() => ({ message: "Saved" })}
    />
  );
}
